import { Component, effect, inject, input } from '@angular/core';
import { NgClass } from '@angular/common';
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { rxResource } from '@angular/core/rxjs-interop';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { of } from 'rxjs';
import { finalize, switchMap, tap } from 'rxjs/operators';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faArrowRight, faCog, faTrash } from '@fortawesome/free-solid-svg-icons';
import { faUser } from '@fortawesome/free-regular-svg-icons';

import { environment } from '../../../../environments/environment';

// Components
import { NavbarComponent } from '../../../template/components/navbar/navbar.component';
import { ConfirmModal } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { ListComponent } from "../../../shared/components/list/list.component";
import { LinkModal } from '../../../shared/components/link-modal/link-modal.component';

// Models
import { ContextDetail, ContextListElem, CreateContext } from '../../models/context.model';
import { Group, GroupListElem } from '../../../groups/models/group.model';

// Services
import { ContextService } from '../../services/context.service';
import { ToastService } from '../../../shared/services/toast.service';
import { GroupService } from '../../../groups/services/group.service';

type EditContextForm = {
  [K in keyof CreateContext]: FormControl<CreateContext[K]>;
};


@Component({
  selector: 'app-context-detail',
  imports: [
    NavbarComponent,
    ReactiveFormsModule,
    FontAwesomeModule,
    NgClass,
    ListComponent
  ],
  templateUrl: './context-detail.component.html'
})
export class ContextDetailComponent {
  private readonly router = inject(Router);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly toastSrv = inject(ToastService);
  private readonly modalSrv = inject(NgbModal);
  private readonly groupSrv = inject(GroupService);
  private readonly contextSrv = inject(ContextService);

  faArrowRight = faArrowRight;
  faUser = faUser;
  faTrash = faTrash;
  faCog = faCog;

  editMode = false;
  loading = false;

  readonly id = input.required<string>();

  // Contesto passato via router state (evita la prima chiamata HTTP)
  private cachedContext = this.router.currentNavigation()?.extras.state?.['context'] as ContextDetail | undefined;

  readonly groupsResource = rxResource({
    stream: () => this.groupSrv.listGroups()
  });
  readonly groups = this.groupsResource.value;

  readonly contextResource = rxResource({
    params: () => this.id(),
    stream: ({ params: id }) => {
      if (this.cachedContext) {
        const ctx = this.cachedContext;
        this.cachedContext = undefined; // usato una sola volta, i reload successivi vanno al backend
        return of(ctx);
      }
      return this.contextSrv.getContextDetail(id);
    }
  });
  readonly contextDetail = this.contextResource.value;

  contextForm: FormGroup<EditContextForm> = this.fb.group({
    name: [{ value: '', disabled: true }, Validators.required],
    description: [{ value: '', disabled: true }]
  });

  navList = [{ label: 'Lista Contesti', routePath: '/contexts' }];

  statusTranslationMap: Record<string, string> = {
    IDLE: $localize`Inizializzazione in corso...`,
    COMPLETED: $localize`Attivo`,
    FAILED: $localize`Fallito`
  };

  integrationStatusTranslationMap: Record<string, string> = {
    FRONTEND_INTEGRATION_ALLOWED: $localize`Frontend e Backend`,
    BACKEND_INTEGRATION_ONLY: $localize`Solo Backend`,
    DISABLED: $localize`Disabilitata`
  };

  constructor() {
    // Sincronizza il form ogni volta che la resource cambia valore
    effect(() => {
      const ctx = this.contextDetail();
      if (ctx) {
        this.contextForm.patchValue(ctx);
      }
    });
  }

  updateContext() {
    const contextId = this.contextDetail()?.id;

    if (this.contextForm.valid && contextId) {
      this.contextSrv.updateContext(this.contextForm.getRawValue(), contextId)
        .subscribe({
          next: () => {
            this.toastSrv.show({ type: 'success', message: 'Contesto aggiornato con successo' });
            this.contextResource.reload(); // il form si ri-sincronizza via effect
            this.toggleEditMode(false);
          },
          error: (error) => console.error(error),
        });
    }
  }

  toggleEditMode(value: boolean) {
    this.editMode = value;
    if (value) {
      this.contextForm.enable();
    } else {
      this.contextForm.disable();
    }
  }

  filterGroups(groups: GroupListElem[], contextGroups: Group[]) {
    return groups.filter(group =>
      !contextGroups.some(inputGroup => inputGroup.id === group.id)
    )
  }

  addGroupToContext(groupId: string) {
    this.apiToggleGroupToContext(groupId, 'add');
  }

  removeGroupFromContext(group: Group, action: 'add' | 'remove') {
    const modalRef = this.modalSrv.open(ConfirmModal, { centered: true, windowClass: 'qai-modal', size: 'lg' });
    modalRef.componentInstance.title = 'Dissocia Utente';
    modalRef.componentInstance.body = `Stai disassociando il gruppo ${group.name} dal contesto ${this.contextForm.controls['name'].getRawValue()}.`
    modalRef.result.then(
      (result) => {
        this.apiToggleGroupToContext(group.id, action);
        console.log(`Closed with: ${result}`);
      },
      () => {
        console.log(`Dismissed`);
      },
    );
  }

  openSettings(context: ContextListElem) {
    this.router.navigate([`/contexts/${context.id}/management`]);
  }

  openGroupDetail(group: Group) {
    this.router.navigate([`/groups/${group.id}`]);
  }

  openSeachLink() {
    const modalRef = this.modalSrv.open(LinkModal, { centered: true, windowClass: 'qai-modal', size: 'lg' });
    modalRef.componentInstance.title = 'Link alla pagina di Search';
    modalRef.componentInstance.body = `Ecco il link diretto alla pagina di ricerca associata al contesto "${this.contextForm.controls['name'].getRawValue()}".`;
    modalRef.componentInstance.link = `${environment.searchUrl}/search/search?apikey=${this.contextDetail()?.apiKey}&contextid=${this.contextDetail()?.id}`;
    modalRef.result.then(
      (result) => {
        console.log(`Closed with: ${result}`);
      },
      () => {
        console.log(`Dismissed`);
      },
    );
  }

  private apiToggleGroupToContext(groupId: string, action: 'add' | 'remove') {
    const contextId = this.contextDetail()?.id;
    if (!contextId) return;

    this.loading = true;

    this.contextSrv.toggleGroupToContext(contextId, { groupId, action }).pipe(
      switchMap(() => this.contextSrv.getContextDetail(contextId)),
      tap((res) => {
        this.toastSrv.show({ type: 'success', message: 'Associazione gruppo / contesto aggiornata' });
        this.contextResource.set(res); // aggiorna la resource senza un secondo fetch
      }),
      finalize(() => this.loading = false)
    ).subscribe({
      error: (error) => console.error(error)
    });
  }
}